import React, { useState } from 'react';
import { Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OrderPageData, OrderPageTypes } from '../../../api';
import { ORDER_PAGE_TYPE_COLORS } from './orderPageTypeColors';
import { useLanguage } from '../../../utils/GeneralUtils';
import { ExperienceCard } from './ExperienceCard';
import type { PartialLabelData } from '../../../api/models/PartialLabelData';

type ExperiencesSectionProps = {
  orderPages: OrderPageData[];
  subtitle?: PartialLabelData;
};

export const ExperiencesSection = ({ orderPages, subtitle }: ExperiencesSectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const [filter, setFilter] = useState<OrderPageTypes | null>(null);

  if (!orderPages.length) {
    return null;
  }

  const hasPrivate = orderPages.some((op) => op.order_page_type === OrderPageTypes.PRIVATE);
  const hasEvent = orderPages.some((op) => op.order_page_type === OrderPageTypes.EVENT);
  const hasCourse = orderPages.some((op) => op.order_page_type === OrderPageTypes.COURSE);
  const typeCount = [hasPrivate, hasEvent, hasCourse].filter(Boolean).length;
  const showFilter = typeCount > 1;

  const filteredOrderPages = !filter ? orderPages : orderPages.filter((op) => op.order_page_type === filter);

  const handleFilterChipClick = (type: OrderPageTypes) => {
    setFilter((prev) => (prev === type ? null : type));
  };

  return (
    <Grid container flexDirection="column" mt={isSmallScreen ? 4 : 8} mb={isSmallScreen ? 6 : 10}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          marginBottom: isSmallScreen ? 2 : 3,
          textAlign: isSmallScreen ? 'center' : 'start',
        }}>
        {t('storefront.exploreOfferings')}
      </Typography>
      {subtitle?.[language] && (
        <Typography
          variant="body1"
          sx={{
            marginBottom: isSmallScreen ? 2 : 3,
            textAlign: isSmallScreen ? 'center' : 'start',
            color: 'text.secondary',
          }}>
          {subtitle[language]}
        </Typography>
      )}
      {showFilter && (
        <Grid container justifyContent={isSmallScreen ? 'center' : 'flex-start'} gap={1.5} mb={isSmallScreen ? 2 : 3}>
          {hasPrivate && (
            <Chip
              label={t('storefront.filterPrivate')}
              onClick={() => handleFilterChipClick(OrderPageTypes.PRIVATE)}
              variant={filter === OrderPageTypes.PRIVATE ? 'filled' : 'outlined'}
              sx={{
                fontSize: 16,
                padding: '20px 8px',
                fontWeight: 500,
                borderColor: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.PRIVATE],
                color: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.PRIVATE],
                ...(filter === OrderPageTypes.PRIVATE && {
                  backgroundColor: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.PRIVATE],
                  color: 'white',
                }),
              }}
            />
          )}
          {hasEvent && (
            <Chip
              label={t('storefront.filterJoinGroup')}
              onClick={() => handleFilterChipClick(OrderPageTypes.EVENT)}
              variant={filter === OrderPageTypes.EVENT ? 'filled' : 'outlined'}
              sx={{
                fontSize: 16,
                padding: '20px 8px',
                fontWeight: 500,
                borderColor: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.EVENT],
                color: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.EVENT],
                ...(filter === OrderPageTypes.EVENT && {
                  backgroundColor: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.EVENT],
                  color: 'white',
                }),
              }}
            />
          )}
          {hasCourse && (
            <Chip
              label={t('storefront.filterCourse')}
              onClick={() => handleFilterChipClick(OrderPageTypes.COURSE)}
              variant={filter === OrderPageTypes.COURSE ? 'filled' : 'outlined'}
              sx={{
                fontSize: 16,
                padding: '20px 8px',
                fontWeight: 500,
                borderColor: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.COURSE],
                color: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.COURSE],
                ...(filter === OrderPageTypes.COURSE && {
                  backgroundColor: ORDER_PAGE_TYPE_COLORS[OrderPageTypes.COURSE],
                  color: 'white',
                }),
              }}
            />
          )}
        </Grid>
      )}
      <Grid
        container
        sx={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? '1fr' : 'repeat(3, 1fr)',
          gap: isSmallScreen ? 2 : 3,
        }}>
        {filteredOrderPages.map((orderPage) => (
          <ExperienceCard key={orderPage.id} orderPage={orderPage} />
        ))}
      </Grid>
    </Grid>
  );
};
